import { Suspense, useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { MRT_ColumnDef } from "material-react-table"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    getEditServiceCenterMessageRoute,
    SERVICE_CENTER_ADMIN_ROUTE,
    SERVICE_CENTER_MESSAGES_CREATE_ROUTE,
} from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    FetchServiceCenterMessagesResponse,
    ServiceCenterMessagesQuery,
    deleteServiceCenterMessage,
    fetchServiceCenterMessages,
} from "../serviceCenterMessagesApi"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { MessagePublishType, ServiceCenterMessage } from "../serviceCenterMessagesTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { LockedDialog } from "../../../../merchant/admin/messages/components/LockedDialog"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { SelectPublishTypeFilter } from "../components/SelectPublishTypeFilter"
import { makeStyles } from "tss-react/mui"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../login/loginSelectors"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}

const useStyles = makeStyles()((theme) => ({
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
}))

const selectMessages = (data: FetchServiceCenterMessagesResponse) => data?.messages || []

function ServiceCenterMessagesTable() {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { classes } = useStyles()

    const [publishType, setPublishType] = useState<MessagePublishType>(MessagePublishType.PUBLISHED)
    const [showLockedDialog, setShowLockedDialog] = useState(false)

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const messagesResult = useApiQuery(fetchServiceCenterMessages, {
        queryName: ServiceCenterMessagesQuery.LIST,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            publishType,
        },
        select: selectMessages,
        enabled: Boolean(serviceCenterId),
    })
    const { ApiQueryTable, columnFactory } = useApiQueryTable(messagesResult)

    const columns = useMemo<MRT_ColumnDef<ServiceCenterMessage>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("messages-table-subject-column"),
                accessorKey: "title",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("messages-table-publish-time-column"),
                    accessorKey: "publishTime",
                },
                (row) => row.publishTime,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("messages-table-expire-time-column"),
                    accessorKey: "expirationDate",
                },
                (row) => row.expirationDate,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeleteMessage } = useApiMutation(deleteServiceCenterMessage, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ServiceCenterMessagesQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message:
                    data?.serviceResponse?.displayMessage ?? l10n.getString("messages-table-delete-success-message"),
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteMessage({ messageId: confirmModalState.itemId })
        } else {
            throw new Error("Message id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteMessage])

    return (
        <>
            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            onClick={() => {
                                if (row.original?.editDetails?.update?.canUpdate) {
                                    navigate(getEditServiceCenterMessageRoute(row.original.id))
                                } else {
                                    setShowLockedDialog(true)
                                }
                                closeMenu()
                            }}
                            key={`update-${row.original.id}`}
                        >
                            <LocalizedStrict id="messages-table-edit-button">
                                <>Edit</>
                            </LocalizedStrict>
                        </MenuItem>,
                        <MenuItem
                            onClick={() => {
                                if (row.original?.editDetails?.delete?.canDelete) {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.title}`,
                                        itemId: row.original.id,
                                    })
                                } else {
                                    setShowLockedDialog(true)
                                }
                                closeMenu()
                            }}
                            className={classes.deleteItem}
                            key={`delete-${row.original.id}`}
                        >
                            <LocalizedStrict id="messages-table-delete-button">
                                <>Delete</>
                            </LocalizedStrict>
                        </MenuItem>,
                    ]}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <SelectPublishTypeFilter value={publishType} onChange={setPublishType} />
                        </StandardTableFilter>
                    )}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    open={confirmModalState.showConfirmModal}
                    typeOfEntity={l10n.getString("messages-table-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    onClose={() => setConfirmModalState(defaultModalState)}
                />
            )}
            {showLockedDialog && <LockedDialog open={showLockedDialog} onClose={() => setShowLockedDialog(false)} />}
        </>
    )
}

export function ServiceCenterMessages() {
    const navigate = useNavigate()

    return (
        <>
            <LocalizedStrict id="service-center-messages-list-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MESSAGES}
                    title="Messages"
                    rightElement={
                        <LocalizedStrict id="messages-list-create-button">
                            <StandardButton onClick={() => navigate(SERVICE_CENTER_MESSAGES_CREATE_ROUTE)}>
                                Create message
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="service-center-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-nav-item-messages">
                            <Breadcrumb>Messages</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ServiceCenterMessagesTable />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
